import {createContext, useContext, useState, type PropsWithChildren} from 'react';
import {createStore, useStore, type StoreApi} from 'zustand';
import type {MeshType, ProjectType} from 'api/generated';
import {produce} from 'immer';
import lz from 'lzutf8';
import equal from 'fast-deep-equal';

const ProjectStoreContext = createContext({});

type StoreType = ProjectType & {
  defaultAssignedMeshes: MeshType[];
  editorRootId: string;
  isEditorDirty: boolean;
  isMeshesDirty: boolean;
  isPublishable: boolean;
  updateEditor: (contentToken: string) => void;
  addMeshes: (meshes: MeshType[]) => void;
  assignMeshes: (assignedMeshes: MeshType[]) => void;
  unassignMeshes: (meshes: MeshType[]) => void;
  updateProject: (data: ProjectType) => void;
  updatePublished: (data: ProjectType) => void;
};

interface IProjectStoreProviderProps extends PropsWithChildren {
  defaultValue: ProjectType;
}

export const ProjectStoreProvider = ({children, defaultValue}: IProjectStoreProviderProps) => {
  const [store] = useState(() =>
    createStore<StoreType>((set, get) => ({
      ...defaultValue,
      editorRootId: 'no-code-renderer',
      defaultAssignedMeshes: defaultValue.meshes?.filter(m => m.assigned) ?? [],
      isEditorDirty: false,
      isMeshesDirty: false,
      isUpdating: false,
      isPublishable: (() => {
        const previewPublishedAt = defaultValue.previewState?.publishedAt;
        const livePublishedAt = defaultValue.liveState?.publishedAt;
        if (!previewPublishedAt) return false;
        if (!livePublishedAt) return true;
        return new Date(livePublishedAt) < new Date(previewPublishedAt);
      })(),
      updateEditor: contentToken => {
        set(
          produce(get(), draft => {
            const defaultContentToken = get().previewState?.contentToken ?? '';
            if (!defaultContentToken) {
              const parsed = JSON.parse(contentToken);
              delete parsed.ROOT;
              const json = JSON.stringify(parsed);
              contentToken = json === '{}' ? '' : json;
            }
            const newContentToken = lz.encodeBase64(lz.compress(contentToken));
            draft.isEditorDirty = defaultContentToken !== newContentToken;
          }),
        );
      },
      addMeshes: meshes => {
        set(
          produce(get(), draft => {
            draft.meshes ||= [];
            for (const mesh of meshes) {
              const index = draft.meshes.findIndex(m => m.uid === mesh.uid);
              if (index === -1) {
                draft.meshes.unshift(mesh);
              }
            }
          }),
        );
      },
      assignMeshes: meshes => {
        set(
          produce(get(), draft => {
            draft.meshes ||= [];
            for (const mesh of meshes) {
              const index = draft.meshes.findIndex(m => m.uid === mesh.uid);
              if (index !== -1) {
                draft.meshes[index].assigned = true;
              }
            }
            draft.isMeshesDirty = !equal(
              draft.defaultAssignedMeshes,
              draft.meshes.filter(m => m.assigned),
            );
          }),
        );
      },
      unassignMeshes: meshes => {
        set(
          produce(get(), draft => {
            draft.meshes ||= [];
            for (const mesh of meshes) {
              const index = draft.meshes.findIndex(m => m.uid === mesh.uid);
              if (index !== -1) {
                draft.meshes[index].assigned = false;
              }
            }
            draft.isMeshesDirty = !equal(
              draft.defaultAssignedMeshes,
              draft.meshes.filter(m => m.assigned),
            );
          }),
        );
      },
      updateProject: data => {
        set(
          produce(get(), draft => {
            draft.meshes ||= [];
            draft.name = data.name;
            draft.slug = data.slug;
            draft.byteSizeLimit = data.byteSizeLimit;
            draft.previewState = data.previewState;
            draft.defaultAssignedMeshes = draft.meshes.filter(m => m.assigned);
            draft.isEditorDirty = false;
            draft.isMeshesDirty = false;
            draft.isPublishable = true;
          }),
        );
      },
      updatePublished: data => {
        set(
          produce(get(), draft => {
            draft.liveState = data.liveState;
            draft.isPublishable = false;
          }),
        );
      },
    })),
  );
  return <ProjectStoreContext.Provider value={store}>{children}</ProjectStoreContext.Provider>;
};

const useProjectStore = <T,>(selector: (state: StoreType) => T): T => {
  const store = useContext(ProjectStoreContext);
  return useStore(store as StoreApi<StoreType>, selector);
};

export default useProjectStore;
